import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { format } from 'date-fns';
import ClientProfile from './ClientProfile';
import { usePermissions } from '../../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../../company-management/constants/permissions';
import BackButton from '../../../shared/components/back-button/BackButton';
import {
  buildClientSlugFields,
  ensureCompanyClientSlug,
  getCompanyClientUrlSegment,
  resolveCompanyClient,
} from '../../../shared/clients/companyClientRouting';
import { setClientArchived } from '../../../shared/clients/clientArchive';
import { deleteClient, getClient, listClients, upsertClient } from '../../../api/companyRecords';
import { toJSDate } from '../../../utils/timestampUtils';
import '../../../company-management/components/employee-profile/styles/employee-profile-workspace.css';
import { FormSkeleton } from '../../../components/loading/skeletons';
import './ClientProfile.css';

const ClientProfileContainer = () => {
  const { clientId: clientParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, companyData } = useAuth();
  const { hasPermission } = usePermissions();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const { setNotification } = useNotification();

  const canEdit = hasPermission(PERMISSIONS.CLIENTS.EDIT);
  const canDelete = hasPermission(PERMISSIONS.CLIENTS.DELETE);

  const formatDate = (timestamp) => {
    const date = toJSDate(timestamp);
    if (!date) return 'Recently joined';
    try {
      return format(date, 'MM/dd/yyyy');
    } catch {
      return 'Recently joined';
    }
  };

  const fetchClient = useCallback(async () => {
    if (!user || !companyData || !clientParam) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const resolved = await resolveCompanyClient(companyData.id, clientParam);
      if (!resolved?.id) {
        setError('Client not found');
        return;
      }

      let nextClient = resolved;

      if (!nextClient.slug) {
        const slug = await ensureCompanyClientSlug(
          companyData.id,
          nextClient.id,
          nextClient
        );
        nextClient = { ...nextClient, slug };
      }

      setClient(nextClient);

      const segment = getCompanyClientUrlSegment(nextClient);
      if (segment && segment !== clientParam) {
        navigate(`/app/overview/clients/profile/${segment}`, {
          replace: true,
          state: location.state,
        });
      }
    } catch (err) {
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  }, [user, companyData, clientParam, navigate, location.state]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const handleBack = () => {
    const returnTo = location.state?.returnTo;
    if (
      typeof returnTo === 'string' &&
      returnTo.startsWith('/app/') &&
      !returnTo.startsWith('//')
    ) {
      navigate(returnTo);
      return;
    }
    navigate('/app/overview/clients');
  };

  const handleClientUpdated = async () => {
    try {
      if (!client?.id || !companyData?.id) return;
      const nextClient = await getClient({ companyId: companyData.id, id: client.id });
      if (nextClient) {
        setClient(nextClient);

        const segment = getCompanyClientUrlSegment(nextClient);
        if (segment && segment !== clientParam) {
          navigate(`/app/overview/clients/profile/${segment}`, {
            replace: true,
            state: location.state,
          });
        }
      }
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to refresh client data',
        severity: 'error'
      });
    }
  };

  const handleSaveClient = async (payload) => {
    if (!client || !companyData?.id) return false;
    setSaving(true);
    try {
      const existingClients = await listClients({ companyId: companyData.id, archived: 'all' });
      const fullName = `${payload.firstName || client.firstName || ''} ${payload.lastName || client.lastName || ''}`.trim();
      const slugFields = buildClientSlugFields(
        { ...client, ...payload, name: fullName || client.name },
        existingClients,
        client.id
      );

      await upsertClient({
        companyId: companyData.id,
        id: client.id,
        client: {
          ...payload,
          ...slugFields,
          name: fullName || client.name,
          searchName: fullName.toLowerCase(),
          updatedAt: new Date().toISOString(),
        },
      });
      const nextClient = await getClient({ companyId: companyData.id, id: client.id });
      if (nextClient) {
        setClient(nextClient);

        const segment = getCompanyClientUrlSegment(nextClient);
        if (segment && segment !== clientParam) {
          navigate(`/app/overview/clients/profile/${segment}`, {
            replace: true,
            state: location.state,
          });
        }
      }
      setNotification({
        open: true,
        message: 'Client updated successfully',
        severity: 'success',
      });
      return true;
    } catch {
      setNotification({
        open: true,
        message: 'Failed to update client. Please try again.',
        severity: 'error',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleArchiveChange = (archived) => {
    if (!canEdit || !client?.id || !companyData?.id) return;
    if (archived) {
      setIsArchiveDialogOpen(true);
      return;
    }
    void handleConfirmArchive(false);
  };

  const handleCloseArchiveDialog = () => {
    if (isArchiving) return;
    setIsArchiveDialogOpen(false);
  };

  const handleConfirmArchive = async (archived = true) => {
    if (!client?.id || !companyData?.id || !canEdit) return;

    setIsArchiving(true);
    try {
      await setClientArchived(companyData.id, client.id, archived);
      const nextClient = await getClient({ companyId: companyData.id, id: client.id });
      if (nextClient) {
        setClient(nextClient);
      }

      setNotification({
        open: true,
        message: archived ? 'Client archived successfully' : 'Client restored successfully',
        severity: 'success',
      });

      setIsArchiveDialogOpen(false);

      if (archived) {
        setTimeout(() => {
          navigate('/app/overview/clients', { state: { listMode: 'archived' } });
        }, 800);
      }
    } catch {
      setNotification({
        open: true,
        message: archived
          ? 'Failed to archive client. Please try again.'
          : 'Failed to restore client. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!client || !companyData) return;
    
    setIsDeleting(true);
    try {
      await deleteClient({ companyId: companyData.id, id: client.id });
      setNotification({
        open: true,
        message: 'Client deleted successfully',
        severity: 'success'
      });
      
      setTimeout(() => {
        navigate('/app/overview/clients');
      }, 1500);
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to delete client. Please try again.',
        severity: 'error'
      });
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="employee-profile-page">
        <FormSkeleton fields={6} label="Loading client" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="employee-profile-page">
        <div className="employee-profile-state">
          <div>{error}</div>
          <div className="employee-profile-state__detail">
            The client you are looking for could not be loaded.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-profile-page">
      <div className="employee-profile-page__toolbar">
        <BackButton
          label="Back to Clients"
          className="back-btn--flush"
          onClick={handleBack}
        />
        {canDelete ? (
          <button
            type="button"
            className="employee-profile-page__btn employee-profile-page__btn--danger"
            onClick={handleDelete}
          >
            Delete Client
          </button>
        ) : null}
      </div>
      <ClientProfile
        client={client}
        canEdit={canEdit}
        formatDate={formatDate}
        onClientUpdated={handleClientUpdated}
        onSaveClient={handleSaveClient}
        saving={saving}
        onArchiveChange={handleArchiveChange}
        archiving={isArchiving}
      />
      
      {isArchiveDialogOpen && (
        <div className="clientProfileDialogBackdrop" onClick={handleCloseArchiveDialog}>
          <div className="clientProfileDialog" onClick={(e) => e.stopPropagation()}>
            <div className="clientProfileDialogHeader">
              <h2>Archive Client</h2>
            </div>
            <div className="clientProfileDialogBody">
              <p>
                Archive <strong>{client?.firstName} {client?.lastName}</strong>? They will be
                hidden from active client lists and booking, but can be restored later from
                Archived clients.
              </p>
            </div>
            <div className="clientProfileDialogActions">
              <button
                type="button"
                className="clientProfileDialogBtn secondary"
                onClick={handleCloseArchiveDialog}
                disabled={isArchiving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="clientProfileDialogBtn danger"
                onClick={() => handleConfirmArchive(true)}
                disabled={isArchiving}
              >
                {isArchiving ? 'Archiving...' : 'Archive Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {isDeleteDialogOpen && (
        <div className="clientProfileDialogBackdrop" onClick={handleCloseDeleteDialog}>
          <div className="clientProfileDialog" onClick={(e) => e.stopPropagation()}>
            <div className="clientProfileDialogHeader">
              <h2>Delete Client</h2>
            </div>
            <div className="clientProfileDialogBody">
              <p>
                Are you sure you want to delete <strong>{client?.firstName} {client?.lastName}</strong>? 
                This action cannot be undone and will permanently remove all client data.
              </p>
            </div>
            <div className="clientProfileDialogActions">
              <button 
                className="clientProfileDialogBtn secondary"
                onClick={handleCloseDeleteDialog} 
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="clientProfileDialogBtn danger"
                onClick={handleConfirmDelete} 
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClientProfileContainer;
